/**
 * ful-config.js — Re-exports from ful-legend.js for backward compatibility.
 *
 * All values originate in ful-legend.js (the single source of truth).
 * New code should import directly from ful-legend.js.
 * Existing imports from this file continue to work unchanged.
 */

import {
  PLANS, CREDITS as LEGEND_CREDITS, REFERRALS as LEGEND_REFERRALS,
  COST_BASIS as LEGEND_COST_BASIS, CIRCUIT_BREAKER, HOT_SEATS,
  IMAGE_GEN_FUL_COST as LEGEND_IMAGE_GEN_FUL_COST,
  IMAGE_GEN_RAW_COSTS as LEGEND_IMAGE_GEN_RAW_COSTS,
  IMAGE_GEN_TIERS as LEGEND_IMAGE_GEN_TIERS,
  MARKUP as LEGEND_MARKUP,
  costForProvider as legendCostForProvider,
  costForTier as legendCostForTier,
  getSpendCap,
} from "./ful-legend";

// ── Metered feature costs ────────────────────────────────────────────
export const MARKUP = LEGEND_MARKUP;
export const IMAGE_GEN_RAW_COSTS = LEGEND_IMAGE_GEN_RAW_COSTS;
export const IMAGE_GEN_TIERS = LEGEND_IMAGE_GEN_TIERS;
export const costForProvider = legendCostForProvider;
export const costForTier = legendCostForTier;
// Back-compat: derived from costForProvider("openai") in legend.
// Existing call sites should migrate to costForProvider(provider).
export const IMAGE_GEN_FUL_COST = LEGEND_IMAGE_GEN_FUL_COST;

// ── Tier definitions: REMOVED 2026-05-26 (User-Type Phase 2K) ────────
// Canonical TIERS registry now lives at `app/lib/tiers.js` (7 tiers +
// effectiveTier() resolver). Consumers import from there directly.
// SEAT_LIMITS removed in same ship — consumers read tier.messages via
// `TIERS[seat_type]?.messages`. Phase 2 sweep migrated 10 consumers
// (commits 7619b5b9 + 465ec302 + bd4cf1a9 + 06b349a8 + 58576502 +
// d3dbc75a + e1b32c4e + 7eb95ea6 + 4dba7d4e + 0788eef6 + 1bc58758).

export const PLAN_LABELS = {
  trial: PLANS.trial.label,
  free: PLANS.trial.label,     // alias
  standard: PLANS.standard.label,
  pro: PLANS.pro.label,
};

export const PLAN_PRICES = {
  trial: PLANS.trial.priceLabel,
  free: PLANS.trial.priceLabel, // alias
  standard: PLANS.standard.priceLabel,
  pro: PLANS.pro.priceLabel,
};

// ── Credits ──────────────────────────────────────────────────────────
export const CREDITS = {
  price: LEGEND_CREDITS.price,
  amount: LEGEND_CREDITS.fulAmount,
  priceLabel: LEGEND_CREDITS.priceLabel,
  description: LEGEND_CREDITS.description,
};

// ── Referrals ────────────────────────────────────────────────────────
export const REFERRALS = {
  fulPerRef: LEGEND_REFERRALS.fulPerRefPerMonth,
  fulPerDollar: LEGEND_REFERRALS.fulPerDollar,
  creditPerRef: LEGEND_REFERRALS.creditPerRef,
  freeAtStandard: LEGEND_REFERRALS.freeStandardThreshold,
  freeAtPro: LEGEND_REFERRALS.freeProThreshold,
  payoutMinTier: LEGEND_REFERRALS.payoutTierMin,
  payoutMinUsd: LEGEND_REFERRALS.payoutMinimum,
  tiers: LEGEND_REFERRALS.tiers,
};

// ── Hot seats ────────────────────────────────────────────────────────
export { HOT_SEATS };

// ── BYOK ─────────────────────────────────────────────────────────────
export const BYOK = {
  platformFee: PLANS.byok.platformFee,
  model: PLANS.byok.model,
  maxTokens: PLANS.byok.maxTokens,
};

// ── Owner ────────────────────────────────────────────────────────────
export const OWNER = {
  model: PLANS.owner.model,
  maxTokens: PLANS.owner.maxTokens,
  compressAt: PLANS.owner.compressAt,
};

// ── Cost basis ───────────────────────────────────────────────────────
export const COST_BASIS = {
  currentCostPerMsg: LEGEND_COST_BASIS.currentCostPerFul,
  targetCostPerMsg: LEGEND_COST_BASIS.targetCostPerFul,
  lastUpdated: LEGEND_COST_BASIS.lastUpdated,
};

// ── Cost ceilings (derived from legend — auto-tightens as costs drop) ─
export const COST_CEILINGS = {
  trial: getSpendCap("trial"),
  free: getSpendCap("trial"), // alias
  standard: getSpendCap("standard"),
  pro: getSpendCap("pro"),
};

// ── Circuit breaker ──────────────────────────────────────────────────
export { CIRCUIT_BREAKER };

// ── Low fuel (deprecated — billing state machine handles this now) ───
export const LOW_FUEL_THRESHOLD = 0.8;
